import { Box } from '@mui/material';
import React, { useMemo } from 'react';
import ElementDataRow from 'components/common/element-data/ElementDataRow';
import {
    ElementMetadataFieldName,
    ElementMetadataSectionName,
    IElementDataCustomFields,
} from 'components/common/element-data/types';
import { useQuery } from '@tanstack/react-query';
import { CustomFieldSection, homePage } from 'api/home-page';
import ElementDataSection from 'components/common/element-data/ElementDataSection';
import ElementDataRowLabel from 'components/common/element-data/ElementDataRowLabel';
import ElementDataRowValue from 'components/common/element-data/ElementDataRowValue';
import {
    getFieldElementMetadataSettings,
    getSectionElementMetadataSettings,
} from 'components/common/element-data/functions';

export default function CustomFields(props: IElementDataCustomFields) {
    const {
        customFieldsNeedToLoad = false,
        customFieldDisplay = {},
        customFields: customFieldsData = [],
        elementType = '',
        elementId = 0,
        settings,
    } = props;
    const fieldKey: ElementMetadataFieldName = 'custom_fields';
    const { fieldMetadataDisplay, fieldMetadataOrder } = getFieldElementMetadataSettings(settings, fieldKey);
    const sectionKey: ElementMetadataSectionName = 'custom_fields';
    const { sectionMetadataOrder } = getSectionElementMetadataSettings(settings, sectionKey);

    const displaySettings = {
        ...{
            used_for_metric_ind: 'Y',
            used_for_multimetric_ind: 'Y',
            used_for_report_ind: 'Y',
            used_for_external_report_ind: 'Y',
            used_for_external_content_ind: 'Y',
            used_for_dataset_ind: 'Y',
        },
        ...customFieldDisplay,
    };

    const showCustomFields: boolean = useMemo(() => {
        switch (elementType) {
            case 'metric':
                return displaySettings['used_for_metric_ind'] == 'Y';
            case 'multi-metric chart':
                return displaySettings['used_for_multimetric_ind'] == 'Y';
            case 'internal report':
                return displaySettings['used_for_report_ind'] == 'Y';
            case 'external report':
                return displaySettings['used_for_external_report_ind'] == 'Y';
            case 'other external content':
                return displaySettings['used_for_external_content_ind'] == 'Y';
            case 'dataset':
            case 'virtual-dataset':
                return displaySettings['used_for_dataset_ind'] == 'Y';
            default:
                return false;
        }
    }, [displaySettings, elementType]);

    const { data: loadedCustomFields, isLoading } = useQuery<Array<CustomFieldSection>, Error>({
        queryKey: [`tile-${elementId}-custom-fields`],
        queryFn: () => {
            return homePage.loadCustomFields(elementId, elementType);
        },
        enabled: fieldMetadataDisplay && customFieldsNeedToLoad && showCustomFields,
    });

    const customFields: Array<CustomFieldSection> = useMemo(() => {
        if (!showCustomFields) return [];
        if (customFieldsNeedToLoad) {
            if (isLoading) return [];
            return loadedCustomFields ?? [];
        }
        return customFieldsData;
    }, [loadedCustomFields, isLoading, customFieldsData, showCustomFields]);

    if (!fieldMetadataDisplay || customFields.length == 0) {
        return null;
    }

    return (
        <>
            {customFields.map((section) => {
                return (
                    <ElementDataSection
                        adjustableDisplayTitle={false}
                        order={sectionMetadataOrder}
                        key={section.cfs_id}
                        title={section.label}
                    >
                        {section.fields.map((field) => {
                            const value = Object.values(field.values).join(', ');
                            return (
                                <ElementDataRow key={field.id}>
                                    <ElementDataRowLabel>{field.name}</ElementDataRowLabel>
                                    <ElementDataRowValue>
                                        {field.type == 'email' ? (
                                            <Box dangerouslySetInnerHTML={{ __html: value }}></Box>
                                        ) : (
                                            <Box
                                                className={'markdown-holder'}
                                                dangerouslySetInnerHTML={{
                                                    __html: value,
                                                }}
                                            />
                                        )}
                                    </ElementDataRowValue>
                                </ElementDataRow>
                            );
                        })}
                    </ElementDataSection>
                );
            })}
        </>
    );
}
